import { request } from '@/utils/request'
import { packCriteria } from '@/utils/filters'

// These correspond to Category values
export const defaultActivityColors = {
    'F.L.A.G. Risk': 'rgb(128,128,255)',
    Incentives: 'rgb(255,160,122)',
    'Insider Transactions': 'rgb(20, 184, 170)',
    'Pledged Shares': 'rgb(163, 204, 0)',
    'Premium Research': 'rgb(10, 92, 17)',
    'Work History': 'rgb(184, 20, 48)',

    default: 'rgb(0, 0, 0)',
}

class ColorMap {
    constructor(colors) {
        this.colors = colors
    }

    getColor(category) {
        return this.colors[category] ?? this.colors.default ?? 'rgb(0, 0, 0)'
    }
}

export function unpack(feed) {
    const results = feed?.results ?? feed
    const facets = feed?.facets ?? []

    const executives = []

    if (results.executives) {
        results.executives.forEach(e => (executives[e.id] = e))
    }

    const companies = []

    if (results.companies) {
        results.companies.forEach(c => (companies[c.id] = c))
    }

    const colors = results.colors ? new ColorMap(results.colors) : new ColorMap(defaultActivityColors)
    const counts = {}

    results.items.forEach(item => {
        item.executive = item.executive || executives[item.executiveId] || null
        item.company = item.company || companies[item.companyId] || null
        item.color = colors.getColor(item.category)
        counts[item.category] = (counts[item.category] || 0) + 1
    })

    if (results.categoryInformation) {
        results.categories = results.categoryInformation.filter(x => x.count != 0)
    } else {
        results.categories = results.categories?.map(category => ({
            name: category,
            color: colors.getColor(category),
            count: counts[category] || 0,
        }))
    }

    results.facets = facets ?? []

    return results
}

async function get(baseUri, filters) {
    return unpack(await request.get(baseUri, filters))
}

async function search(
    url,
    criteria,
    sortField = null,
    sortDirection = null,
    offset = 0,
    limit = 50,
    abortSignal = null
) {
    const params = {
        'sort.field': sortField,
        'sort.direction': sortDirection,
        'page.offset': offset,
        'page.limit': limit,
    }

    const response = await request.post(
        url,
        {
            criteria: packCriteria(criteria),
            offset,
            limit,
        },
        { signal: abortSignal },
        params
    )

    if (response.ok) {
        let result = unpack(response.content)

        return {
            ...result,
            totalCount: response.content.meta.resultCount,
        }
    }

    throw response
}

export default {
    async listFacets(signal = null) {
        let facets = (await search('/api/activity/search/executives', [], null, null, 0, 0, signal)).facets
        facets = facets.filter(f => f.group === 'Activity')
        return facets
    },
    async searchCompanies(
        criteria,
        sortField = null,
        sortDirection = null,
        offset = 0,
        limit = 50,
        abortSignal = null
    ) {
        let url = '/api/activity/search/companies'
        return await search(url, criteria, sortField, sortDirection, offset, limit, abortSignal)
    },
    async searchExecutives(
        criteria,
        sortField = null,
        sortDirection = null,
        offset = 0,
        limit = 50,
        abortSignal = null
    ) {
        let url = '/api/activity/search/executives'
        return await search(url, criteria, sortField, sortDirection, offset, limit, abortSignal)
    },
    async searchWatchlist(
        watchlistId,
        criteria,
        sortField = null,
        sortDirection = null,
        offset = 0,
        limit = 50,
        abortSignal = null
    ) {
        let url = `/api/activity/search/tracklists/${watchlistId}`
        return await search(url, criteria, sortField, sortDirection, offset, limit, abortSignal)
    },
    getByCompany(id, filters) {
        return get(`/api/activity/companies/${id}`, filters).then(data => {
            data.items = data.items.filter(item => item.company)
            return data
        })
    },
    getDetails(id) {
        return request.get(`/api/activity/${id}/details`)
    },
}
